import type { Dispatch, SetStateAction } from "react";
import type { UseFormReturn } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  BriefCreatorTypeEnum,
  briefCreatorTypeEnumZod,
} from "@nlc/shared-types";
import { useSearch } from "@tanstack/react-router";
import { convert } from "html-to-text";
import { useEffect, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import * as z from "zod";

import {
  ignoreHrefOptions,
  REQUEST_DESCRIPTION_FIELD,
  TEXTS_FOR_DESIGN_FIELD,
  tooLongTextRefine,
} from "@/features/request/create/shared/characterLimit";
import { useIsMBCSubmitted } from "@/features/request/create/shared/useIsMBCSubmitted";
import { useIsRequestFormDirty } from "@/hooks/useIsRequestFormDirty";
import { useUser } from "@/hooks/useUser";

import { fileAssetSchema, linkAssetSchema } from "../../shared/utils";

const createGraphicRequestFormSchema = (userRole: string) =>
  z.object({
    /* eslint-disable perfectionist/sort-objects */
    requestName: z
      .string()
      .min(1, { message: "Request name is required" })
      .max(255, { message: "Request name is too long" }),
    designType: z
      .number({
        required_error: "Design type is required",
      })
      .nullable()
      .refine((val): val is number => val !== null, {
        message: "Design type is required",
      }),
    brand: z.number().optional().nullable(),
    // Brief creator
    briefCreatorType: briefCreatorTypeEnumZod,
    briefCreatorDetailed: z.object({
      additionalGuidelines: z.string(),
      brandGuidelines: z.string(),
      creativeGoal: z.string(),
      keyMessageAndCta: z.string(),
      scope: z.string(),
      finalBriefHTML: z
        .string()
        .refine((value) => convert(value, ignoreHrefOptions).length < 5000, {
          // 5000 characters of text (html tags removed)
          message: "Final brief text is too long",
        }),
      finalBrief: z.string(),
    }),
    briefCreatorSingle: z.object({
      requestDescriptionHTML: z
        .string({ required_error: "Request description is required" })
        .superRefine(
          tooLongTextRefine(
            REQUEST_DESCRIPTION_FIELD.label,
            REQUEST_DESCRIPTION_FIELD.convertOptions,
          ),
        ),
      textsForDesignHTML: z
        .string({ required_error: "Text for design is required" })
        .superRefine(
          tooLongTextRefine(
            TEXTS_FOR_DESIGN_FIELD.label,
            TEXTS_FOR_DESIGN_FIELD.convertOptions,
          ),
        ),
      requestDescription: z.string(),
      textsForDesign: z.string(),
    }),
    // Assets and inspirations
    assets: z.array(z.union([fileAssetSchema, linkAssetSchema])),
    inspirations: z.array(z.union([fileAssetSchema, linkAssetSchema])),
    // Technical details
    creativeDirection: z
      .union([
        z.literal("Designer has creative freedom"),
        z.literal("Follow brief exactly"),
      ])
      .optional(),
    sizes: z.string().min(1, { message: "Sizes are required" }).max(800, {
      message: "Sizes are too long",
    }),
    fileTypes: z
      .string()
      .min(1, { message: "File types are required" })
      .max(250, {
        message: "File types are too long",
      }),
    preferredDesignerIds: z.array(z.number()),
    designComplexity: [
      "customer-success",
      "super-admin",
      "team-leader",
    ].includes(userRole)
      ? z.number({ required_error: "Design complexity is required" })
      : z.any().optional(),
    restrictAiAssets: z.boolean().optional(),
    collaboratorIds: z.array(z.number()),
    /* eslint-enable perfectionist/sort-objects */
  });

export type CreateGraphicRequestFormData = z.infer<
  ReturnType<typeof createGraphicRequestFormSchema>
>;

export function useCreateGraphicRequestForm(): {
  setShowWarningModal: Dispatch<SetStateAction<boolean>>;
  showWarningModal: boolean;
} & UseFormReturn<CreateGraphicRequestFormData> {
  const { data: user } = useUser();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const setIsDirty = useIsRequestFormDirty((state) => state.setIsDirty);
  const isMBCSubmitted = useIsMBCSubmitted((state) => state.isSubmitted);

  const form = useForm<CreateGraphicRequestFormData>({
    defaultValues: {
      /* eslint-disable perfectionist/sort-objects */
      requestName: "",
      designType: undefined,
      brand: undefined,
      briefCreatorType: BriefCreatorTypeEnum.SINGLE,
      briefCreatorDetailed: {
        additionalGuidelines: "",
        brandGuidelines: "",
        creativeGoal: "",
        keyMessageAndCta: "",
        scope: "",
        finalBriefHTML: "",
        finalBrief: "",
      },
      briefCreatorSingle: {
        requestDescriptionHTML: "",
        requestDescription: "",
        textsForDesignHTML: "",
        textsForDesign: "",
      },
      assets: [],
      inspirations: [],
      creativeDirection: undefined,
      sizes: "",
      fileTypes: "",
      preferredDesignerIds: [],
      designComplexity: undefined,
      restrictAiAssets: false,
      collaboratorIds: [],
      /* eslint-enable perfectionist/sort-objects */
    },
    resolver: zodResolver(
      createGraphicRequestFormSchema(user?.user.roleName || "").superRefine(
        (data, ctx) => {
          switch (data.briefCreatorType) {
            case BriefCreatorTypeEnum.DETAILED: {
              const currentValues = form.getValues();

              // Don't validate if there is final brief text
              if (
                convert(
                  currentValues.briefCreatorDetailed.finalBriefHTML,
                ).trim().length > 0
              ) {
                return;
              }
              // if AI generated - validate input field
              if (
                isMBCSubmitted &&
                convert(
                  currentValues.briefCreatorDetailed.finalBriefHTML,
                ).trim().length === 0
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: "Request description is required",
                  path: ["briefCreatorDetailed", "finalBrief"],
                });
              }

              break;
            }
            case BriefCreatorTypeEnum.SINGLE: {
              const currentValues = form.getValues();

              // Check for empty requestDescription
              if (
                convert(
                  currentValues.briefCreatorSingle.requestDescriptionHTML,
                ).trim().length === 0
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: "Request description is required",
                  path: ["briefCreatorSingle", "requestDescription"],
                });
              }
              // Check for empty textsForDesign
              if (
                convert(
                  currentValues.briefCreatorSingle.textsForDesignHTML,
                ).trim().length === 0
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: "Text for design is required",
                  path: ["briefCreatorSingle", "textsForDesign"],
                });
              }

              break;
            }
          }
        },
      ),
    ),
  });

  const { brand_id } = useSearch({
    from: "/_auth/_app-shell/requests/create",
  });

  useEffect(() => {
    if (brand_id) form.setValue("brand", brand_id);
  }, [brand_id, form.setValue]);

  useEffect(() => {
    setIsDirty(form.formState.isDirty);
  }, [form.formState.isDirty, setIsDirty]);

  useEffect(() => {
    const unsubscribe = useIsRequestFormDirty.subscribe(
      (state) => state.isDirty,
      (isDirty) => {
        if (!isDirty) {
          form.reset(form.getValues(), { keepErrors: true });
        }
      },
    );

    return () => unsubscribe();
  }, [form]);

  return {
    ...form,
    setShowWarningModal,
    showWarningModal,
  };
}

export function useCreateGraphicRequestFormContext() {
  return useFormContext<CreateGraphicRequestFormData>();
}
export { BRIEF_CREATOR_SINGLE_FIELDS as CHARACTER_LIMIT_FIELDS } from "@/features/request/create/shared/characterLimit";
